import React, { useCallback, useState } from "react";
import { GoogleMap, Marker, useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import { Search, MapPin } from "lucide-react";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const LIBRARIES = ["places"];
const DEFAULT_CENTER = { lat: 26.2389, lng: 73.0243 }; // Jodhpur

/**
 * Google Maps location picker (drop-in replacement for the earlier Leaflet component).
 * Props preserved: lat, lng, onPick(lat, lng), readOnly, height
 */
export default function MapPicker({ lat, lng, onPick, readOnly = false, height = 320 }) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || "",
    libraries: LIBRARIES,
  });
  const [map, setMap] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);

  const center = lat != null && lng != null ? { lat, lng } : DEFAULT_CENTER;

  const onLoad = useCallback((m) => setMap(m), []);
  const onUnmount = useCallback(() => setMap(null), []);

  const handleClick = (e) => {
    if (readOnly) return;
    onPick(e.latLng.lat(), e.latLng.lng());
  };

  const handleMarkerDrag = (e) => {
    if (readOnly) return;
    onPick(e.latLng.lat(), e.latLng.lng());
  };

  const onPlaceChanged = () => {
    if (!autocomplete) return;
    const place = autocomplete.getPlace();
    if (place.geometry && place.geometry.location) {
      const newLat = place.geometry.location.lat();
      const newLng = place.geometry.location.lng();
      onPick(newLat, newLng);
      if (map) {
        map.panTo({ lat: newLat, lng: newLng });
        map.setZoom(15);
      }
    }
  };

  if (loadError) {
    return (
      <div className="rounded-xl border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive" style={{ height }}>
        Google Maps failed to load. Check your API key.
      </div>
    );
  }
  if (!isLoaded) {
    return (
      <div className="rounded-xl border border-border bg-accent/40 grid place-items-center animate-pulse" style={{ height }}>
        <MapPin size={20} className="text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="relative rounded-xl overflow-hidden border border-border shadow-sm" style={{ height }} data-testid="map-container">
      {!readOnly && (
        <div className="absolute top-2 left-2 right-2 z-10 max-w-md">
          <Autocomplete
            onLoad={(ac) => setAutocomplete(ac)}
            onPlaceChanged={onPlaceChanged}
            options={{ componentRestrictions: { country: "in" } }}
          >
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                placeholder="Address ya area search karein..."
                className="w-full h-10 pl-9 pr-3 rounded-md border border-border bg-white shadow text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="map-search-input"
              />
            </div>
          </Autocomplete>
        </div>
      )}
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={center}
        zoom={lat != null && lng != null ? 15 : 11}
        onClick={handleClick}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          gestureHandling: readOnly ? "cooperative" : "greedy",
          clickableIcons: false,
        }}
      >
        {lat != null && lng != null && (
          <Marker
            position={{ lat, lng }}
            draggable={!readOnly}
            onDragEnd={handleMarkerDrag}
          />
        )}
      </GoogleMap>
    </div>
  );
}
